import React, { useState, useRef, useEffect } from 'react';
import icon from '@ingka/ssr-icon/paths/magnifying-glass';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import styled from 'styled-components';
import useTranslation from '../../hooks/useTranslation';
import { addressAutocompleteService } from '../../services';
import { SA_SHORT_ADDRESS_ENDPOINT } from '../../services/AddressAutoCompleteService';
const SA_SHORT_ADDRESS_REGEX = /^[A-Z]{4}[0-9]{4}$/;

const AddressContainer = styled.div<{ $fullwidth?: boolean }>`
  position: relative;
  width: ${({ $fullwidth }) => ($fullwidth ? '100%' : 'calc(50% - 10px)')};
`;

const AddressCard = styled.div`
  background-color: #f7f7f7;
  border-radius: 8px;
  padding: 16px;
  font-family: 'Noto IKEA', 'Noto Sans', 'Roboto', 'Open Sans', sans-serif;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const AddressText = styled.p<{ $compact?: boolean }>`
  margin: ${({ $compact }) => ($compact ? '0 0 4px' : '0 0 8px')};
  line-height: ${({ $compact }) => ($compact ? '1.2' : '1.4')};
  font-size: 1rem;
  color: #333;
`;

const SearchContainer = styled.div`
  font-family: 'Noto IKEA', 'Noto Sans', 'Roboto', 'Open Sans', sans-serif;
`;

const Dropdown = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: -20px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: #fff;

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #aaa;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #888;
  }
`;

const DropdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #eaeaea;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f1f1f1;
  }
`;

const ItemInfo = styled.div`
  max-width: calc(100% - 30px);
`;

const ItemText = styled.p`
  display: block;
  color: #333;
  font-size: 1rem;
`;

const SubText = styled.p`
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #666;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  color: #888;
  font-size: 1.125rem;
`;

const Suggestion = styled.div`
  margin-top: -20px;
  padding: 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #666;
`;

const ValidationMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgb(224, 7, 81); /* Matching the validation error color */
  font-size: 0.75rem;
  margin-top: -17px;

  span {
    display: inline-block;
    width: 16px;
    height: 16px;
    font-weight: bold;
    background-color: rgb(224, 7, 81); /* Red circle */
    border-radius: 50%; /* Circle shape */
    position: relative;
    text-align: center;
    line-height: 16px; /* Vertically center the content */
    font-size: 0.75rem; /* Adjust size of the exclamation mark */
    color: white; /* White exclamation mark */
  }
`;

const HelperText = styled.div`
  margin-top: -14px;
  margin-bottom: 12px;
  font-size: 0.75rem;
  color: #666;
`;

export type AddressDetails = {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  postalCode: string;
  state: string;
};

type AddressAutoCompleteProps = {
  onAddressSelect: (details: AddressDetails) => void;
  submitAddressFlag: boolean;
  onLookupStateChange?: (isSuccess: boolean) => void;
  fullwidth?: boolean;
};

const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z0-9]/g, '');

const getValueByKeys = (source: Record<string, any>, keys: string[]): string => {
  const normalizedMap = Object.keys(source || {}).reduce(
    (acc: Record<string, any>, key) => {
      acc[normalizeKey(key)] = source[key];
      return acc;
    },
    {}
  );

  for (const key of keys) {
    const normalized = normalizeKey(key);
    if (
      Object.prototype.hasOwnProperty.call(normalizedMap, normalized) &&
      normalizedMap[normalized] !== undefined &&
      normalizedMap[normalized] !== null
    ) {
      return String(normalizedMap[normalized]);
    }
  }
  return '';
};

const joinAddressParts = (left: string, right: string): string => {
  if (left && right) {
    return `${left}, ${right}`;
  }
  return left || right || '';
};

export const AddressAutoCompleteComponent = ({
  onAddressSelect,
  submitAddressFlag,
  onLookupStateChange,
  fullwidth,
}: AddressAutoCompleteProps) => {
  const isSaudiMarket = CSS_CONFIG.COUNTRY.toLowerCase() === 'sa';
  const endpoint = isSaudiMarket
    ? SA_SHORT_ADDRESS_ENDPOINT
    : 'address/autocomplete';
  const isArabic = CSS_CONFIG.LANGUAGE.toLowerCase() === 'ar';
  const [value, setValue] = useState<string>('');
  const [loadingFlag, setLoadingFlag] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [suggestedData, setSuggestedData] = useState<
    { name?: string; value?: string }[]
  >([]);

  const [isAddressSelected, setIsAddressSelected] = useState<boolean>(false);
  const [moreInputRequired, setMoreInputRequired] = useState<boolean>(false);

  const [lookupFailed, setLookupFailed] = useState<boolean>(false);
  const [displayRows, setDisplayRows] = useState<string[]>([]);

  const [selectedAddress, setSelectedAddress] = useState<AddressDetails>({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    postalCode: '',
    state: '',
  });
  const { t } = useTranslation();
  const addressLabel = t('spareParts.AddressLabel');
  const inputRef = useRef<HTMLInputElement>(null);

  const latestSaudiLookupRef = useRef<string>('');

  const setLookupState = (isSuccess: boolean) => {
    if (onLookupStateChange) {
      onLookupStateChange(isSuccess);
    }
  };

  const handleSaudiShortAddressResponse = (res: any, shortAddress: string) => {
    const addressRecords = res?.Addresses || [];
    const payload = Array.isArray(addressRecords) && addressRecords.length > 0
      ? addressRecords[0]
      : null;

    if (!payload) {
      const errorMessage =
        res?.statusdescription ||
        t('spareParts.invalidShortAddress');
      throw new Error(errorMessage);
    }
    const buildingNumber = getValueByKeys(payload, ['BuildingNumber', 'Building_Number']);
    const additionalNumber = getValueByKeys(payload, [
      'AdditionalNumber',
      'Additional Number',
      'Additional_Number',
    ]);
    const streetL2 = getValueByKeys(payload, ['Street_L2', 'StreetL2']);
    const districtL2 = getValueByKeys(payload, ['District_L2', 'DistrictL2']);
    const cityL2 = getValueByKeys(payload, ['City_L2', 'CityL2']);
    const postalCode = getValueByKeys(payload, ['PostCode', 'Post_Code', 'PostalCode']);

    const streetAr = getValueByKeys(payload, ['Street']);
    const districtAr = getValueByKeys(payload, ['District']);
    const cityAr = getValueByKeys(payload, ['City']);

    if (!buildingNumber || !streetL2 || !districtL2 || !cityL2 || !postalCode) {
      throw new Error('Short address response does not contain required fields');
    }

    const selectedAddress = {
      addressLine1: joinAddressParts(buildingNumber, streetL2),
      addressLine2: joinAddressParts(additionalNumber, districtL2),
      addressLine3: shortAddress,
      city: cityL2,
      postalCode,
      state: '',
    };

    const countryDisplay = isArabic
      ? 'المملكة العربية السعودية'
      : 'Saudi Arabia';

    const row1 = isArabic
      ? joinAddressParts(buildingNumber, streetAr || streetL2)
      : selectedAddress.addressLine1;
    const row2 = isArabic
      ? joinAddressParts(additionalNumber, districtAr || districtL2)
      : selectedAddress.addressLine2;
    const row4 = isArabic ? cityAr || cityL2 : cityL2;

    setDisplayRows([row1, row2, postalCode, row4, countryDisplay].filter(Boolean));
    setSelectedAddress(selectedAddress);
    setValue(shortAddress.toUpperCase());
    setSuggestedData([]);
    setLookupFailed(false);
    setIsAddressSelected(true);
    setLookupState(true);
    onAddressSelect(selectedAddress);
  };

  useEffect(() => {
    if (isAddressSelected && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      input.selectionStart = input.selectionEnd = input.value.length;
    }
  }, [isAddressSelected]);

  const mapSelectedAddress = async (item: any) => {
     if (isSaudiMarket) {
      return;
    }

    if (item.isCapturable === true) {
      const res = await addressAutocompleteService.calladdressautocomplete({
        captureId: item.captureId,
        freeText: item.text,
        endpoint,
      });
      setIsAddressSelected(true);
      setLookupState(true);
      setSuggestedData([]);
      const selectedAddress = {
        addressLine1: res.addressLine1,
        addressLine2: res.addressLine2,
        addressLine3: res.addressLine3,
        city: res.city,
        postalCode: res.postalCode,
        state: res.state,
      };
      setSelectedAddress(selectedAddress);
      setValue(
        `${res.addressLine1} ${res.addressLine2} ${res.addressLine3} ${res.city} ${res.postalCode} ${res.state}`
      );
      onAddressSelect(selectedAddress);
    }
    if (item.isCapturable === false) {
      const res = await addressAutocompleteService.calladdressautocomplete({
        captureGroupId: item.captureGroupId,
        freeText: item.text,
        endpoint,
      });
      if (res && res.suggestions) {
        setSuggestedData(res.suggestions);
      } else {
        setSuggestedData([]);
      }
      setValue(`${item.text}`);
    }
  };

  const onFieldChange = (event: Event) => {
    setSuggestedData([]);
    setLoadingFlag(false);
    setMoreInputRequired(false);
    setIsAddressSelected(false);

    setLookupFailed(false);
    setDisplayRows([]);
    setLookupState(false);

    const target = event.target as HTMLInputElement;
    const freeText = target.value;

    const normalizedFreeText = isSaudiMarket
      ? freeText.toUpperCase().replace(/\s/g, '').slice(0, 8)
      : freeText;
    if (isSaudiMarket) {
      latestSaudiLookupRef.current = normalizedFreeText;
    }

    setValue(normalizedFreeText);

    if (!normalizedFreeText) {
      setSuggestedData([]);
      return;
    } else if (isSaudiMarket && !SA_SHORT_ADDRESS_REGEX.test(normalizedFreeText)) {
      setMoreInputRequired(true);
      setSuggestedData([]);
      return;
    } else if (!isSaudiMarket && normalizedFreeText.length < 3) {
      setMoreInputRequired(true);
      setSuggestedData([]);
      return;
    } else if (isSaudiMarket || normalizedFreeText.length >= 3) {
      setLoadingFlag(true);
      addressAutocompleteService
        .calladdressautocomplete({
          freeText: normalizedFreeText,
          endpoint,
        })
        .then((res) => {
          if (isSaudiMarket) {
            if (latestSaudiLookupRef.current !== normalizedFreeText) {
              return;
            }
            handleSaudiShortAddressResponse(res, normalizedFreeText);
            } else {
            setSuggestedData(res.suggestions);
          }
          setLoadingFlag(false);
        })
        .catch(() => {
          if (isSaudiMarket && latestSaudiLookupRef.current !== normalizedFreeText) {
            return;
          }
          setLoadingFlag(false);
          setSuggestedData([]);
          if (isSaudiMarket) {
            setLookupFailed(true);
          }
        });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex < suggestedData.length - 1 ? prevIndex + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestedData.length - 1
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestedData.length) {
        mapSelectedAddress(suggestedData[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      if (suggestedData.length > 0) {
        event.preventDefault();
        setSuggestedData([]);
        setActiveIndex(-1);
      }
    }
  };

  const showRequiredError =
    submitAddressFlag &&
    !moreInputRequired &&
    !loadingFlag &&
    !isAddressSelected &&
    !lookupFailed &&
    suggestedData.length === 0;

  const showLookupError = lookupFailed;

  return (
    <AddressContainer $fullwidth={fullwidth}>
      <SearchContainer>
        <FormField>
          <InputField
            id="address-autocomplete"
            type="text"
            label={addressLabel}
            ssrIcon={icon}
            iconPosition="leading"
            value={value}
            ref={inputRef}
            onChange={onFieldChange}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-label={addressLabel}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-controls={
              suggestedData.length ? 'suggestions-list' : undefined
            }
            aria-expanded={!!suggestedData.length}
            aria-activedescendant={
              activeIndex >= 0 ? `suggestion-item-${activeIndex}` : undefined
            }
            className={`${submitAddressFlag ? 'input-invalid' : 'input-valid'}`}
            maxLength={isSaudiMarket ? 8 : undefined}
            autoComplete="off"
            name="no-autofill"
            required
          />
        </FormField>

        {isSaudiMarket && !showRequiredError && !showLookupError && !isAddressSelected && (
          <HelperText>{t('spareParts.shortAddressHelperText')}</HelperText>
        )}

        {showRequiredError && (
            <ValidationMessage role="alert">
              <span aria-hidden="true">!</span>{t('spareParts.AddressfieldValidation')}
            </ValidationMessage>
          )}

          {showLookupError && (
          <ValidationMessage role="alert">
            <span aria-hidden="true">!</span>{t('spareParts.invalidShortAddress')}
          </ValidationMessage>
        )}

        {moreInputRequired && !isSaudiMarket ? (
          <Suggestion aria-live="polite">{t('spareParts.typeMore')}</Suggestion>
        ) : (
          ''
        )}
        {loadingFlag && !isSaudiMarket ? (
          <Suggestion>{t('spareParts.loadingData')}</Suggestion>
        ) : (
          ''
        )}
        {suggestedData.length ? (
          <Dropdown id="suggestions-list" role="listbox">
            {suggestedData.map((item: any, index: number) => (
              <DropdownItem
                key={item.value ? item.value : `suggestion-${index}`}
                id={`suggestion-item-${index}`}
                role="option"
                tabIndex={-1}
                aria-selected={activeIndex === index}
                onClick={() => mapSelectedAddress(item)}
                style={{
                  backgroundColor:
                    activeIndex === index ? '#f1f1f1' : 'transparent',
                }}
              >
                <ItemInfo>
                  <ItemText>{item.text}</ItemText>
                  <SubText>{item.subText}</SubText>
                </ItemInfo>
                <ArrowIcon aria-hidden="true">&#10132;</ArrowIcon>
              </DropdownItem>
            ))}
          </Dropdown>
        ) : (
          ''
        )}
      </SearchContainer>
      {isAddressSelected ? (
        <AddressCard>
          {displayRows.length > 0 ? (
            displayRows.map((row: string, index: number) => (
              <AddressText $compact={isSaudiMarket} key={`${row}-${index}`}>
                {row}
              </AddressText>
            ))
          ) : (
            <>
              <AddressText $compact={isSaudiMarket}>{`${selectedAddress.addressLine1} ${selectedAddress.addressLine2} ${selectedAddress.addressLine3}`}</AddressText>
              <AddressText $compact={isSaudiMarket}>{`${selectedAddress.city} ${selectedAddress.postalCode} ${selectedAddress.state}`}</AddressText>
            </>
          )}
        </AddressCard>
      ) : (
        ''
      )}
    </AddressContainer>
  );
};
